import type { AlertLayoutPreset, FlowEditorState, FlowGridItem, FlowNodeKind, FlowViewportDevice } from "./types";
import { DEFAULT_VIEWPORT, FLOW_CANVAS_PADDING, RGL_CONTAINER_PADDING, RGL_MARGIN, RGL_ROW_HEIGHT, gridSettings, viewportPresets } from "./constants";
import { getAlertLayoutPresetDefinition } from "./constants";
export const isFlowViewportDevice = (value: unknown): value is FlowViewportDevice =>
  value === "lg" ||
  value === "md" ||
  value === "sm" ||
  value === "xs" ||
  value === "xxs";

export const getFlowViewport = (
  viewport?: Partial<Omit<FlowEditorState["viewport"], "device">> & {
    device?: unknown;
  },
): FlowEditorState["viewport"] => {
  const device = isFlowViewportDevice(viewport?.device)
    ? viewport.device
    : DEFAULT_VIEWPORT.key;
  const preset =
    viewportPresets.find((item) => item.key === device) || DEFAULT_VIEWPORT;

  return {
    x: Number(viewport?.x) || 0,
    y: Number(viewport?.y) || 0,
    zoom: Number(viewport?.zoom) || preset.zoom,
    width: Number(viewport?.width) || preset.width,
    height: Number(viewport?.height) || preset.height,
    device,
  };
};

export const getGridRows = (viewport: FlowEditorState["viewport"]) => {
  const config = gridSettings[viewport.device];
  const innerHeight = Math.max(
    viewport.height - RGL_CONTAINER_PADDING[1] * 2,
    1,
  );

  return Math.max(
    Math.round(
      (innerHeight + config.margin) / (RGL_ROW_HEIGHT + config.margin),
    ),
    1,
  );
};

export function getGridConfig(
  device: FlowViewportDevice,
): (typeof gridSettings)[FlowViewportDevice];
export function getGridConfig(
  viewport: FlowEditorState["viewport"],
): (typeof gridSettings)[FlowViewportDevice];
export function getGridConfig(
  value: FlowViewportDevice | FlowEditorState["viewport"],
) {
  const isViewport = typeof value !== "string";
  const device = isViewport ? value.device : value;
  const config = gridSettings[device];

  if (!isViewport) return config;

  const rows = getGridRows(value);
  const innerHeight = Math.max(
    value.height -
      RGL_CONTAINER_PADDING[1] * 2 -
      config.margin * Math.max(rows - 1, 0),
    rows,
  );

  return {
    ...config,
    rowHeight: innerHeight / rows,
  };
}

export const getFittedViewportZoom = (
  viewport: FlowEditorState["viewport"],
  containerWidth: number,
  containerHeight: number,
) => {
  const availableWidth = Math.max(containerWidth - FLOW_CANVAS_PADDING, 240);
  const availableHeight = Math.max(containerHeight - FLOW_CANVAS_PADDING, 220);
  const nextZoom = Math.min(
    availableWidth / viewport.width,
    availableHeight / viewport.height,
    1,
  );

  return Math.max(Number(nextZoom.toFixed(3)), 0.2);
};

export const getGridColumnWidth = (viewport: FlowEditorState["viewport"]) => {
  const config = getGridConfig(viewport.device);
  return (
    (viewport.width -
      RGL_CONTAINER_PADDING[0] * 2 -
      config.margin * (config.cols - 1)) /
    config.cols
  );
};

export const getDefaultGridSize = (
  kind: FlowNodeKind,
  device: FlowViewportDevice,
): Pick<FlowGridItem, "w" | "h"> => {
  const cols = getGridConfig(device).cols;

  if (kind === "leaderboard") return { w: Math.min(cols, 4), h: 4 };
  if (kind === "chat") return { w: Math.min(cols, 4), h: 4 };
  if (kind === "goal") return { w: Math.min(cols, 5), h: 2 };
  if (kind === "alert") return { w: Math.min(cols, 4), h: 3 };
  if (kind === "timer") return { w: Math.min(cols, 3), h: 2 };
  if (kind === "image") return { w: Math.min(cols, 3), h: 3 };
  return { w: Math.min(cols, 3), h: 1 };
};

export const getAlertPresetGridSize = (
  preset: AlertLayoutPreset,
  device: FlowViewportDevice,
): Pick<FlowGridItem, "w" | "h"> => {
  const cols = getGridConfig(device).cols;
  const isCompactViewport = device === "xs" || device === "xxs";

  if (preset === "compact") {
    return { w: Math.min(cols, isCompactViewport ? cols : 3), h: 1 };
  }

  if (preset === "text-only") {
    return { w: Math.min(cols, isCompactViewport ? cols : 6), h: 2 };
  }

  if (preset === "banner") {
    return { w: Math.min(cols, isCompactViewport ? cols : 7), h: 2 };
  }

  if (preset === "icon-left" || preset === "icon-right") {
    return { w: Math.min(cols, isCompactViewport ? cols : 5), h: 2 };
  }

  if (preset === "center") {
    return { w: Math.min(cols, isCompactViewport ? cols : 5), h: 4 };
  }

  return { w: Math.min(cols, isCompactViewport ? cols : 4), h: 3 };
};

export const getDefaultGridSizeForSettings = (
  kind: FlowNodeKind,
  device: FlowViewportDevice,
  settings?: Record<string, any>,
): Pick<FlowGridItem, "w" | "h"> => {
  if (kind !== "alert") return getDefaultGridSize(kind, device);

  const preset = getAlertLayoutPresetDefinition(
    settings?.layoutPreset || settings?.layout,
    settings?.theme,
  );

  return getAlertPresetGridSize(preset.key, device);
};
